import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import { canModifyContent } from '@/lib/account-roles';
import {
  usePerformersQuery,
  usePerformerTypesQuery,
  useSavePerformerMutation,
} from '@/queries/performers';
import { useAuthStore } from '@/stores/auth-store';
import {
  HOME_TABS,
  useHomeDashboardStore,
} from '@/stores/home-dashboard-store';
import { FestivalHeader, Loading } from '../controls';
import { PerformerForm } from './PerformerForm';

export function PerformerFormPage(props) {
  const history = useHistory();
  const formRef = useRef(null);
  const currentUser = useAuthStore((s) => s.currentUser);
  const canModify = canModifyContent(currentUser);
  const setActiveTab = useHomeDashboardStore((s) => s.setActiveTab);

  const id = props.match?.params?.id;
  const numericId =
    id !== undefined && id !== '' && !Number.isNaN(Number(id))
      ? String(id)
      : undefined;
  const isNew = !numericId;

  const performersQ = usePerformersQuery();
  const typesQ = usePerformerTypesQuery();
  const saveMutation = useSavePerformerMutation();

  const loadedPerformer = useMemo(() => {
    if (!numericId) {
      return null;
    }
    const list = performersQ.data ?? [];
    return (
      list.find((p) => String(p.performerId) === String(numericId)) ?? null
    );
  }, [performersQ.data, numericId]);

  const [overrides, setOverrides] = useState({});

  useEffect(() => {
    setOverrides({});
  }, [numericId, isNew]);

  const performer = isNew
    ? overrides
    : { ...(loadedPerformer ?? {}), ...overrides };

  const onFileUpload = (fileInfo) => {
    const { url, filename } = fileInfo;
    setOverrides((prev) => ({
      ...prev,
      photoURL: url,
      performerPhoto: filename,
    }));
  };

  const onFileDelete = () => {
    setOverrides((prev) => ({
      ...prev,
      photoURL: null,
      performerPhoto: null,
    }));
  };

  const handleSave = async (values) => {
    if (!canModify) {
      return;
    }
    try {
      const saved = await saveMutation.mutateAsync({
        ...performer,
        ...values,
      });
      const savedId = saved?.performerId ?? performer.performerId ?? numericId;
      if (savedId) {
        history.push(`/performer/${savedId}/view`);
      } else {
        setActiveTab(HOME_TABS.performers);
        history.push('/');
      }
    } catch {
      /* mutation toast */
    }
  };

  if (!canModify) {
    if (numericId) {
      return <Redirect to={`/performer/${numericId}/view`} />;
    }
    return <Redirect to="/" />;
  }

  if (!isNew && performersQ.isPending) {
    return <Loading />;
  }

  if (!isNew && (performersQ.isError || !loadedPerformer)) {
    return (
      <div className="py-16 text-center text-red-600">
        Could not load this performer.
      </div>
    );
  }

  const headerTitle = isNew
    ? 'Add Performer'
    : performer.performerName || 'Edit Performer';

  return (
    <div>
      <FestivalHeader
        title={headerTitle}
        showSave
        showExit
        onSave={() => formRef.current?.requestSubmit()}
        onExit={() => setActiveTab(HOME_TABS.performers)}
      />
      <div className="w-full">
        <div className="event-main-info min-w-0">
          <PerformerForm
            formRef={formRef}
            className="admin-form"
            performer={performer}
            performerTypes={typesQ.data ?? {}}
            handleSave={handleSave}
            onFileUpload={onFileUpload}
            onFileDelete={onFileDelete}
            isPending={saveMutation.isPending}
          />
        </div>
      </div>
    </div>
  );
}
